import {useCallback, useState} from 'react'
import {useLogsQuery} from './useLogs'
import {usePendingPrompt} from './use-pending-prompt'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'

export interface UseCLIPromptStateOptions {
  /** The session ID to monitor for prompts */
  sessionId: string | undefined
  /** Whether to enable the hook (typically: isCLI && !!sessionId) */
  enabled: boolean
}

export interface CLIPromptState {
  /** The current pending CLI prompt, or null if none */
  pendingPrompt: NormalizedPrompt | null
  /** Whether logs are still loading */
  isLoading: boolean
  /** Call this when the user responds to a prompt to prevent it from resurrecting on refetch */
  markPromptResolved: () => void
  /** Dismiss the current prompt without marking it as resolved */
  dismissPrompt: () => void
}

/**
 * Hook that monitors a CLI session for pending prompts.
 */
export function useCLIPromptState({sessionId, enabled}: UseCLIPromptStateOptions): CLIPromptState {
  // Track prompt IDs that have been responded to, preventing resurrection on refetch
  const [resolvedPromptIds, setResolvedPromptIds] = useState<Set<string>>(() => new Set())

  const {data: logs, isLoading} = useLogsQuery({
    sessionId,
    enabled: enabled && !!sessionId,
  })

  const {pendingPrompt} = usePendingPrompt(logs, resolvedPromptIds)

  const markPromptResolved = useCallback(() => {
    if (pendingPrompt) {
      setResolvedPromptIds(prev => new Set(prev).add(pendingPrompt.toolCallId))
    }
  }, [pendingPrompt])

  const dismissPrompt = useCallback(() => {
    // For dismiss, we add to resolved set so it doesn't come back
    if (pendingPrompt) {
      setResolvedPromptIds(prev => new Set(prev).add(pendingPrompt.toolCallId))
    }
  }, [pendingPrompt])

  return {
    pendingPrompt,
    isLoading,
    markPromptResolved,
    dismissPrompt,
  }
}
